/**
 * Neural Network Visualization for Morpheus-RL
 * 
 * This script generates a visual representation of the neural network
 * that powers the Morpheus-RL tutoring system, specifically showing
 * how it adapts to different learning styles.
 */

// Initialize the neural network visualization
function initializeNeuralVisualization(canvasId) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;
  
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  
  // Clear the canvas
  ctx.clearRect(0, 0, width, height);
  
  return {
    canvas,
    ctx,
    width,
    height,
    neurons: [],
    connections: []
  };
}

// Generate neurons for the visualization
function generateNeurons(network, config) {
  if (!network) return;
  
  const { width, height } = network;
  const { layerCount, neuronsPerLayer, dominantStyle } = config;
  
  // Clear existing neurons
  network.neurons = [];
  
  // Generate new neurons
  for (let layer = 0; layer < layerCount; layer++) {
    const layerNeurons = [];
    const neuronsInThisLayer = layer === 0 || layer === layerCount - 1 
      ? neuronsPerLayer 
      : neuronsPerLayer + Math.floor(Math.random() * 3);
    
    const layerX = (width * 0.1) + (layer * (width * 0.8) / (layerCount - 1));
    
    for (let n = 0; n < neuronsInThisLayer; n++) {
      const neuronY = height * 0.1 + (n * (height * 0.8) / (neuronsInThisLayer - 1));
      
      // Determine neuron type/role
      let type = 'hidden';
      let style = null;
      
      if (layer === 0) {
        type = 'input';
        // Assign input neurons to different learning styles
        if (n < neuronsInThisLayer / 4) style = 'visual';
        else if (n < neuronsInThisLayer / 2) style = 'auditory';
        else if (n < neuronsInThisLayer * 3/4) style = 'kinesthetic';
        else style = 'reading';
      } else if (layer === layerCount - 1) {
        type = 'output';
      }
      
      // Create neuron object
      const neuron = {
        x: layerX,
        y: neuronY,
        type,
        style,
        active: Math.random() > 0.3, // Some neurons are inactive
        strength: Math.random() * 0.8 + 0.2 // Random strength
      };
      
      // Make neurons of the dominant style more active
      if (style === dominantStyle.toLowerCase()) {
        neuron.active = true;
        neuron.strength = Math.random() * 0.3 + 0.7; // Higher strength
      }
      
      layerNeurons.push(neuron);
    }
    
    network.neurons.push(layerNeurons);
  }
}

// Generate connections between neurons
function generateConnections(network, config) {
  if (!network) return;
  
  // Clear existing connections
  network.connections = [];
  
  const { dominantStyle } = config;
  
  // For each layer (except the last)
  for (let layer = 0; layer < network.neurons.length - 1; layer++) {
    const currentLayer = network.neurons[layer];
    const nextLayer = network.neurons[layer + 1];
    
    // Connect each neuron in the current layer to some neurons in the next layer
    for (let i = 0; i < currentLayer.length; i++) {
      const sourceNeuron = currentLayer[i];
      
      // Determine how many connections to make
      let connectionCount = Math.floor(Math.random() * 3) + 1;
      
      // Create connections to random neurons in the next layer
      for (let c = 0; c < connectionCount; c++) {
        const targetIndex = Math.floor(Math.random() * nextLayer.length);
        const targetNeuron = nextLayer[targetIndex];
        
        // Skip if both neurons are inactive
        if (!sourceNeuron.active && !targetNeuron.active) continue;
        
        // Create connection with a random strength
        let strength = Math.random() * 0.7 + 0.1;
        
        // Strengthen connections related to the dominant style
        if (sourceNeuron.style === dominantStyle.toLowerCase()) {
          strength += 0.2;
        }
        
        network.connections.push({
          source: sourceNeuron,
          target: targetNeuron,
          strength: strength,
          active: sourceNeuron.active && targetNeuron.active
        });
      }
    }
  }
}

// Render the neural network on the canvas
function renderNeuralNetwork(network, animationFrame = 0) {
  if (!network) return;
  
  const { ctx, width, height } = network;
  
  // Clear canvas
  ctx.clearRect(0, 0, width, height);
  
  // Draw connections
  for (const conn of network.connections) {
    if (!conn.active) continue;
    
    const { source, target, strength } = conn;
    
    // Calculate alpha based on strength
    const alpha = Math.min(0.9, strength + Math.sin(animationFrame * 0.05) * 0.1);
    
    // Set line style
    ctx.strokeStyle = `rgba(100, 149, 237, ${alpha})`;
    ctx.lineWidth = strength * 3;
    
    // Draw line
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    
    // Draw signal pulse animation
    if (Math.random() < 0.02) { // Randomly show pulses
      drawSignalPulse(network, source, target, strength);
    }
  }
  
  // Draw neurons
  for (const layer of network.neurons) {
    for (const neuron of layer) {
      if (!neuron.active) continue;
      
      const { x, y, type, style, strength } = neuron;
      
      // Set fill style based on neuron type
      let fillStyle;
      let size;
      
      switch (type) {
        case 'input':
          size = 8;
          switch (style) {
            case 'visual': fillStyle = 'rgb(40, 167, 69)'; break; // Green
            case 'auditory': fillStyle = 'rgb(13, 110, 253)'; break; // Blue
            case 'kinesthetic': fillStyle = 'rgb(255, 193, 7)'; break; // Yellow
            case 'reading': fillStyle = 'rgb(23, 162, 184)'; break; // Cyan
            default: fillStyle = 'rgb(108, 117, 125)'; // Gray
          }
          break;
        case 'output':
          size = 8;
          fillStyle = 'rgb(220, 53, 69)'; // Red
          break;
        default:
          size = 6;
          fillStyle = 'rgb(108, 117, 125)'; // Gray
      }
      
      // Draw neuron circle
      ctx.fillStyle = fillStyle;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw active neurons with a glow effect
      if (strength > 0.6) {
        ctx.globalAlpha = 0.3 + Math.sin(animationFrame * 0.1) * 0.2;
        ctx.beginPath();
        ctx.arc(x, y, size + 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
    }
  }
}

// Draw a pulse traveling along a connection
function drawSignalPulse(network, source, target, strength) {
  const { ctx } = network;
  
  // Calculate a position along the line
  const t = (Math.sin(Date.now() * 0.01) + 1) / 2; // Value between 0 and 1
  const x = source.x + (target.x - source.x) * t;
  const y = source.y + (target.y - source.y) * t;
  
  // Draw pulse
  ctx.fillStyle = `rgba(255, 255, 255, ${strength})`;
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();
}

// Public API for the neural network visualizer
const NeuralNetworkVisualizer = {
  create: function(canvasId, config) {
    const network = initializeNeuralVisualization(canvasId);
    if (!network) return null;
    
    // Default configuration
    const defaultConfig = {
      layerCount: 4,
      neuronsPerLayer: 8,
      dominantStyle: 'Visual'
    };
    
    const finalConfig = { ...defaultConfig, ...(config || {}) };
    
    generateNeurons(network, finalConfig);
    generateConnections(network, finalConfig);
    
    let animationFrame = 0;
    let animationId = null;
    
    function animate() {
      renderNeuralNetwork(network, animationFrame);
      animationFrame++;
      animationId = requestAnimationFrame(animate);
    }
    
    return {
      start: function() {
        if (!animationId) {
          animate();
        }
        return this;
      },
      stop: function() {
        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
        return this;
      },
      updateConfig: function(newConfig) {
        Object.assign(finalConfig, newConfig);
        generateNeurons(network, finalConfig);
        generateConnections(network, finalConfig);
        return this;
      }
    };
  }
};

export default NeuralNetworkVisualizer;